import { Fragment, useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { FIELDS, SECTIONS, ROLES, ACTIONS } from "@/lib/fields";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Save } from "lucide-react";

const EDITABLE_ROLES = ROLES.filter((r) => r.id !== "master_admin");
const FIELDS_BY_SECTION = Object.fromEntries(SECTIONS.map((s) => [s.id, FIELDS.filter((f) => f.section === s.id)]));

export default function Permissions() {
  const [matrix, setMatrix] = useState(null);
  const [changed, setChanged] = useState(new Set());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/permissions").then((r) => setMatrix(r.data.matrix)).catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- api/toast are stable imports

  if (!matrix) return <div className="text-sm text-[#4B5563]" data-testid="permissions-loading">Loading permission matrix...</div>;

  const toggle = (role, kind, key) => {
    setMatrix((m) => {
      const next = { ...m, [role]: { ...m[role] } };
      const list = new Set(next[role][kind]);
      if (list.has(key)) list.delete(key); else list.add(key);
      next[role][kind] = Array.from(list);
      if (kind === "view" && !list.has(key)) {
        next[role].edit = next[role].edit.filter((k) => k !== key);
      }
      return next;
    });
    setChanged((c) => new Set(c).add(role));
  };

  const save = async () => {
    setSaving(true);
    try {
      for (const role of changed) {
        await api.put(`/permissions/${role}`, matrix[role]);
      }
      toast.success("Permissions saved");
      setChanged(new Set());
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  const RoleCell = ({ role, kind, idKey }) => (
    <td className="py-1 px-1 text-center border-l border-[#F3F4F6]">
      <Checkbox checked={matrix[role][kind].includes(idKey)} onCheckedChange={() => toggle(role, kind, idKey)}
        className="rounded-sm" data-testid={`perm-${kind}-${role}-${idKey}`} />
    </td>
  );

  let saveLabel = "No Changes";
  if (changed.size > 0) saveLabel = `Save ${changed.size} role(s)`;
  if (saving) saveLabel = "Saving...";

  return (
    <div className="space-y-4" data-testid="permissions-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-extrabold text-[#111827] tracking-tight">Permission Matrix</h1>
          <p className="text-sm text-[#4B5563] mt-1">
            Field-level <b>V</b>iew / <b>E</b>dit control per role. Master Admin always has full access and is locked.
          </p>
        </div>
        <Button onClick={save} disabled={saving || changed.size === 0}
          className="rounded-sm bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="save-permissions-button">
          <Save className="h-4 w-4 mr-2" /> {saveLabel}
        </Button>
      </div>

      <div className="bg-white border border-[#E5E7EB] overflow-x-auto" data-testid="permissions-matrix-wrapper">
        <table className="text-xs whitespace-nowrap" data-testid="permissions-matrix">
          <thead>
            <tr className="border-b border-[#E5E7EB] bg-[#F9FAFB]">
              <th className="sticky left-0 bg-[#F9FAFB] text-left text-[10px] font-bold uppercase tracking-widest text-[#4B5563] py-2 px-3 border-r border-[#E5E7EB] min-w-[240px]">
                Field / Action
              </th>
              {EDITABLE_ROLES.map((r) => (
                <th key={r.id} className="py-2 px-1 text-center border-l border-[#E5E7EB]">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-[#111827]">{r.label}</div>
                  <div className="text-[9px] text-[#9CA3AF] font-semibold mt-0.5">V&nbsp;&nbsp;/&nbsp;&nbsp;E below</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {SECTIONS.map((section) => (
              <Fragment key={section.id}>
                <tr className="bg-[#111827]">
                  <td colSpan={EDITABLE_ROLES.length + 1} className="sticky left-0 py-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-white">
                    {section.label}
                  </td>
                </tr>
                {FIELDS_BY_SECTION[section.id].map((f) => (
                  <tr key={f.key} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                    <td className="sticky left-0 bg-white py-1 px-3 border-r border-[#E5E7EB] font-medium text-[#111827]">
                      {f.label}{(f.computed || f.auto) && <span className="text-[9px] text-[#9CA3AF] ml-1">(auto)</span>}
                    </td>
                    {EDITABLE_ROLES.map((r) => (
                      <td key={r.id} className="py-1 px-1 border-l border-[#F3F4F6]">
                        <div className="flex items-center justify-center gap-2">
                          <Checkbox checked={matrix[r.id].view.includes(f.key)} onCheckedChange={() => toggle(r.id, "view", f.key)}
                            className="rounded-sm" data-testid={`perm-view-${r.id}-${f.key}`} />
                          <Checkbox checked={matrix[r.id].edit.includes(f.key)} onCheckedChange={() => toggle(r.id, "edit", f.key)}
                            disabled={!!f.computed || !!f.auto} className="rounded-sm border-[#2563EB]" data-testid={`perm-edit-${r.id}-${f.key}`} />
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </Fragment>
            ))}
            <tr className="bg-[#111827]">
              <td colSpan={EDITABLE_ROLES.length + 1} className="sticky left-0 py-1.5 px-3 text-[10px] font-bold uppercase tracking-widest text-white">
                Actions
              </td>
            </tr>
            {ACTIONS.map((a) => (
              <tr key={a.id} className="border-b border-[#F3F4F6] hover:bg-[#F9FAFB]">
                <td className="sticky left-0 bg-white py-1 px-3 border-r border-[#E5E7EB] font-medium text-[#111827]">{a.label}</td>
                {EDITABLE_ROLES.map((r) => (
                  <td key={r.id} className="py-1 px-1 text-center border-l border-[#F3F4F6]">
                    <Checkbox checked={matrix[r.id].actions.includes(a.id)} onCheckedChange={() => toggle(r.id, "actions", a.id)}
                      disabled={a.id === "manage_permissions"} className="rounded-sm" data-testid={`perm-action-${r.id}-${a.id}`} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-[#9CA3AF]">Left checkbox = View, right (blue) = Edit. Edit requires View and is auto-removed when View is revoked.</p>
    </div>
  );
}
